import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import { withTranslation } from "react-i18next"
import { Col, Form, FormFeedback, Input, Label, Row, Spinner } from "reactstrap"
import { cpf } from "cpf-cnpj-validator"
import { useFormik } from "formik"
import * as Yup from "yup"
import { useHistory, useParams } from "react-router-dom"
import { getProfile, getRoles, updateUser } from "services/users/calls"
import { getAllChannels } from "services/support/calls"
import toast from "components/Toast/Toast"

const UpdateUser = props => {
  let { id } = useParams()
  let history = useHistory()
  const [isLoading, handleLoading] = useState(true)
  const [roles, setRoles] = useState([])
  const [canais, setCanais] = useState([])
  const [user, setUser] = useState({})

  const fetchRoles = async () => {
    try {
      const res = await getRoles()
      const { roles } = res.payload
      setRoles(roles)
    } catch (err) {
    } finally {
      handleLoading(false)
    }
  }

  const fetchChannels = async () => {
    try {
      const canais = await getAllChannels()
      setCanais(canais)
    } catch (error) {
      console.log(error)
    }
  }

  const fetchUser = async id => {
    try {
      const {
        payload: { user },
      } = await getProfile(id)
      setUser(user)
    } catch (err) {
    } finally {
      handleLoading(false)
    }
  }

  useEffect(() => {
    fetchRoles()
    fetchChannels()
    fetchUser(id)
  }, [])

  const onUpdateUser = async values => {
    try {
      await updateUser(user.id, values)
      history.push(`/configuracoes/acessos/usuarios`)
    } catch (error) {
      throw error
    }
  }

  const validation = useFormik({
    enableReinitialize: true,

    initialValues: {
      login: user?.login,
      name: user?.name,
      role: user?.roleId,
      email: user?.email,
      documentId: user?.documentId,
      isMfaEnabled: user?.isMfaEnabled,
    },
    validationSchema: Yup.object().shape(
      {
        login: Yup.string()
          .min(3, "Login muito pequeno")
          .max(30, "Máximo de 30 caracteres permitido para Login.")
          .required("Campo usuário é obrigatório."),
        role: Yup.string().required("Campo tipo de acesso é obrigatório"),
        name: Yup.string()
          .min(3, "Nome muito pequeno")
          .max(255, "Máximo de 255 caracteres permitido para Nome.")
          .required("Campo nome é obrigatório."),
        email: Yup.string().email().notRequired().nullable(),
        documentId: Yup.string()
          .notRequired()
          .nullable()
          .when("documentId", {
            is: value => value?.length,
            then: rule =>
              rule.test("", "CPF inválido", value => cpf.isValid(value)),
          }),
      },
      [["documentId", "documentId"]]
    ),
    onSubmit: async (values, { resetForm }) => {
      if (isLoading) return
      handleLoading(true)
      try {
        await onUpdateUser(values)
        toast("success", "Usuário atualizado com sucesso!", "")
        resetForm()
      } catch (error) {
        toast("error", error?.message, error?.title || "Erro")
      } finally {
        handleLoading(false)
      }
    },
  })

  return (
    <React.Fragment>
      <Row>
        <p>Atualização de Cadastro</p>
      </Row>
      <Row>
        <Form
          className="form-horizontal"
          onSubmit={e => {
            e.preventDefault()
            validation.handleSubmit()
            return false
          }}
        >
          <div className="mb-3">
            <Label className="form-label">Dados de Acesso</Label>
            <Row>
              <Col sm={6}>
                <Input
                  name="login"
                  className="form-control"
                  placeholder="Usuário"
                  readOnly
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.login || ""}
                  invalid={
                    validation.touched.login && validation.errors.login
                      ? true
                      : false
                  }
                  autoComplete="off"
                />
                {validation.touched.login && validation.errors.login ? (
                  <FormFeedback type="invalid">
                    {validation.errors.login}
                  </FormFeedback>
                ) : null}
              </Col>
              <Col sm={4}>
                <Input
                  name="role"
                  className="form-control"
                  placeholder="Tipo de Acesso"
                  type="select"
                  readOnly={user?.roleId === 1}
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.role || ""}
                  //   value={user?.roleId}
                  invalid={
                    validation.touched.role && validation.errors.role
                      ? true
                      : false
                  }
                >
                  <option value={""}>Tipo de Acesso</option>
                  {user?.roleId === 1 && <option value={1}>Super Admin</option>}
                  {roles.map(role => (
                    <option value={role.id} key={`role-${role.id}`}>
                      {role.name}
                    </option>
                  ))}
                </Input>
                {validation.touched.role && validation.errors.role ? (
                  <FormFeedback type="invalid">
                    {validation.errors.role}
                  </FormFeedback>
                ) : null}
              </Col>
            </Row>
            <Row>
              <Col>
                <div className="my-3 form-check form-switch form-switch-lg">
                  <Label className="form-check-label">
                    Autenticador Google?
                  </Label>
                  <Input
                    name="isMfaEnabled"
                    type="checkbox"
                    className="form-check-input my-0"
                    style={{ marginLeft: "0px" }}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.isMfaEnabled || false}
                    checked={validation.values.isMfaEnabled || false}
                    invalid={
                      validation.touched.isMfaEnabled &&
                      validation.errors.isMfaEnabled
                        ? true
                        : false
                    }
                  />
                  {validation.touched.isMfaEnabled &&
                  validation.errors.isMfaEnabled ? (
                    <FormFeedback type="invalid">
                      {validation.errors.isMfaEnabled}
                    </FormFeedback>
                  ) : null}
                </div>
              </Col>
            </Row>
          </div>
          <div className="mb-3">
            <Label className="form-label">Dados Pessoais</Label>
            <Input
              name="name"
              className="form-control"
              placeholder="Nome Completo"
              onChange={validation.handleChange}
              onBlur={validation.handleBlur}
              value={validation.values.name || ""}
              invalid={
                validation.touched.name && validation.errors.name ? true : false
              }
              autoComplete="off"
            />
            {validation.touched.name && validation.errors.name ? (
              <FormFeedback type="invalid">
                {validation.errors.name}
              </FormFeedback>
            ) : null}
          </div>
          <div className="mb-1">
            <Row>
              <Col>
                <Input
                  name="email"
                  className="form-control"
                  placeholder="E-mail (Opcional)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.email || ""}
                  invalid={
                    validation.touched.email && validation.errors.email
                      ? true
                      : false
                  }
                  autoComplete="off"
                />
                {validation.touched.email && validation.errors.email ? (
                  <FormFeedback type="invalid">
                    {validation.errors.email}
                  </FormFeedback>
                ) : null}
              </Col>
              <Col>
                <Input
                  name="documentId"
                  className="form-control"
                  placeholder="CPF (opcional)"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.documentId || ""}
                  invalid={
                    validation.touched.documentId &&
                    validation.errors.documentId
                      ? true
                      : false
                  }
                  autoComplete="off"
                />
                {validation.touched.documentId &&
                validation.errors.documentId ? (
                  <FormFeedback type="invalid">
                    {validation.errors.documentId}
                  </FormFeedback>
                ) : null}
              </Col>
            </Row>
          </div>
          {isLoading && (
            <div className="mt-3" style={{ textAlign: "center" }}>
              <Spinner className="ms-2" color="primary" />
            </div>
          )}
          {!isLoading && (
            <div className="mt-3 d-grid">
              <button className="btn btn-primary btn-block" type="submit">
                Atualizar
              </button>
            </div>
          )}
        </Form>
      </Row>
    </React.Fragment>
  )
}

UpdateUser.propTypes = {
  t: PropTypes.any,
  reRenderList: PropTypes.bool,
  setReRenderList: PropTypes.func,
  setNav: PropTypes.func,
  userId: PropTypes.number,
}

export default withTranslation()(UpdateUser)
